import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button } from 'antd';

import NozomiGuardianProvisioningModal from '@totem/components/settings/sections/services/provisioning/threat_detection/NozomiGuardianProvisioningModal';
import ServiceAddModal from '@totem/components/settings/sections/services/serviceAddModal';
import ServicesTable from '@totem/components/settings/sections/services/servicesTable';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Service } from '@totem/types/organization';
import { AvailableService } from '@totem/types/services';
import { ORGANIZATION_SERVICES_ENDPOINT } from '@totem/utilities/endpoints';

import './services.css';

const Services = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadServices, setLoadServices] = useState<boolean>(true);
  const [organizationServices, setOrganizationServices] = useState<Service[]>(
    [],
  );
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [isSending, setIsSending] = useState(false);
  const [showThreatDetectionProvisioning, setShowThreatDetectionProvisioning] =
    useState<boolean>(false);
  const [selectedService, setSelectedService] = useState<Service>(null);
  const canProvision =
    permissions.systemAdministrator.hasPermissions(userProfile);

  const filterOrgServices = (orgServices: Service[]) => {
    return orgServices.filter((svc) => svc.isDeleted === false);
  };

  useEffect(() => {
    setLoadServices(true);
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (loadServices) {
      fetch(ORGANIZATION_SERVICES_ENDPOINT, {
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result) => {
          setOrganizationServices(filterOrgServices(result));
        })
        .then(() => setIsLoading(false));

      setLoadServices(false);
    }
  }, [loadServices, getApiToken]);

  const sendAddServiceToOrganization = useCallback(
    async (service: AvailableService) => {
      if (isSending) {
        return;
      }

      setIsSending(true);

      fetch(ORGANIZATION_SERVICES_ENDPOINT, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(service),
      })
        .then((res) => res.json())
        .then((result) => {
          setOrganizationServices(filterOrgServices(result));
        })
        .then(() => {
          setIsSending(false);
        });
    },
    [isSending],
  );

  const sendRemoveServiceFromOrganization = useCallback(
    async (serviceId: string) => {
      if (isSending) {
        return;
      }

      setIsSending(true);

      fetch(`${ORGANIZATION_SERVICES_ENDPOINT}/${serviceId}`, {
        method: 'DELETE',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result) => {
          setOrganizationServices(filterOrgServices(result));
        })
        .then(() => {
          setIsSending(false);
        });
    },
    [isSending],
  );

  const handleOnRemove = (service: Service) => {
    sendRemoveServiceFromOrganization(service.id);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
  };

  const handleOnCreate = (availableService: AvailableService) => {
    sendAddServiceToOrganization(availableService);

    handleCloseModal();
  };

  const handleShowThreatDetectionProvisioning = (service: Service) => {
    setSelectedService(service);

    setShowThreatDetectionProvisioning(true);
  };

  const handleCloseThreatDetectionProvisioning = (refreshServices: Boolean) => {
    setShowThreatDetectionProvisioning(false);
    if (refreshServices) {
      setLoadServices(true);
    }
  };

  const handleShowProvisioning = (service: Service) => {
    if (
      service.name === 'threat_detection' ||
      service.name === 'inventory_and_threat_detection'
    ) {
      handleShowThreatDetectionProvisioning(service);
    }
  };

  return (
    <IBCard
      title="Services"
      isCovered
      extra={
        <Button
          type="primary"
          onClick={() => {
            setShowAddModal(true);
          }}
        >
          Add Service
        </Button>
      }
    >
      <ServicesTable
        loading={isLoading}
        canProvision={canProvision}
        organizationServices={organizationServices}
        onRemove={handleOnRemove}
        onShowProvisioning={handleShowProvisioning}
      />
      {showAddModal && canProvision && (
        <ServiceAddModal
          organizationServices={organizationServices}
          visible={showAddModal}
          onSubmit={handleOnCreate}
          onClose={handleCloseModal}
        />
      )}
      {showThreatDetectionProvisioning && (
        <NozomiGuardianProvisioningModal
          visible={showThreatDetectionProvisioning}
          onClose={handleCloseThreatDetectionProvisioning}
          service={selectedService}
        />
      )}
    </IBCard>
  );
};

export default Services;
